/**
 * UI-first fixtures behind the @api registry.
 *
 * A registry entry that has no backend yet returns `fixture('orders.list', params)`.
 * The axios instance in lib/api.js routes those requests here instead of the
 * network, so pages call fetchApi('merchantOrders') exactly as they will once the
 * real endpoint exists. Wiring an endpoint = change ONE registry entry; the page
 * does not change.
 *
 * Each surface registers its own handlers (client/src/fixtures/<surface>.js):
 *   registerFixtures({ 'orders.list': (params) => ({ data: [...] }) })
 * Handlers receive the request params/body and return the response BODY
 * (the same shape the real endpoint will return: { data, ... }).
 * Throw fixtureError(404, 'Not found') to simulate an error.
 */

const PREFIX = '/__fixtures/';
const handlers = new Map();
const LATENCY_MS = Number(import.meta.env.VITE_FIXTURE_LATENCY_MS ?? 250);

export function registerFixtures(map) {
  for (const [key, fn] of Object.entries(map)) {
    if (handlers.has(key)) throw new Error(`Fixture "${key}" registered twice`);
    handlers.set(key, fn);
  }
}

/** A registry entry served by a fixture handler. `params` reach the handler unchanged. */
export function fixture(key, params = {}, method = 'POST') {
  return { url: `${PREFIX}${key}`, method, data: params };
}

export function isFixtureUrl(url) {
  return typeof url === 'string' && url.startsWith(PREFIX);
}

export function fixtureError(status, message) {
  const e = new Error(message);
  e.status = status;
  return e;
}

/** Axios adapter: resolves /__fixtures/<key> from the registry, after a small latency. */
export async function fixtureAdapter(config) {
  const key = config.url.slice(PREFIX.length);
  const handler = handlers.get(key);
  let body = config.data;
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body);
    } catch {
      /* keep the raw string */
    }
  }
  await new Promise((r) => setTimeout(r, LATENCY_MS));
  const respond = (status, data) => ({ data, status, statusText: String(status), headers: {}, config, request: {} });
  if (!handler) {
    const err = new Error(`No fixture registered for "${key}"`);
    err.response = respond(404, { error: err.message });
    throw err;
  }
  try {
    return respond(200, await handler(body || {}));
  } catch (e) {
    const status = e.status || 500;
    const err = new Error(e.message);
    err.response = respond(status, { error: e.message });
    throw err;
  }
}

/** Deterministic pseudo-random for stable fixture data across reloads. */
export function seeded(seed = 1) {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}
